/**
 * Loads the fax questionnaire for an order, tracks its answers, and drives the
 * submit/poll/navigate flow so {@link ProcedureQuestionaireLayout} stays
 * presentational.
 *
 * Reads are PG (`submission.questionnaire_details`); Firestore is only used
 * when org id is missing so PG cannot be queried. On Submit, answers are
 * dual-written (Firestore + Postgres) before
 * `/fax/submit` so fax fill (still Firestore-backed) sees reviewer edits. The
 * submitted form id comes from the persisted submission row, so a refresh on
 * this page cannot drop it — see {@link resolveFaxFormId}.
 *
 * @module procedureQuestionaire/hooks/useProcedureQuestionnaire
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { submitFaxFormQuestionaire } from "../../../api/postCall/submitFaxFormQuestionaire";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { useUpdateOrderDetails } from "../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { getAllQuestionsForOrder } from "../../../redux/slice/formQuestions-slice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { logError } from "../../../utils/customLogger";
import {
  clearFaxPdfPoller,
  getFaxPdfLinkRecursively,
} from "../../../utils/getFaxPdfLinkRecursively";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { UseProcedureQuestionnaireReturn } from "../types";
import { resolveFaxFormId } from "../utils/faxFormIdResolver";
import { persistFaxQuestionnaire } from "../utils/faxQuestionnairePersistence";
import { useFaxQuestionnaireLoader } from "./useFaxQuestionnaireLoader";

/** Payload for dual-writing reviewer edits onto `submission.questionnaire_details`. */
interface QuestionnaireSubmissionUpdate {
  questionnaire_details: Record<string, unknown>;
}

/**
 * Orchestrates fax questionnaire loading, answer capture, and submission for
 * the order identified by the `id` route param.
 *
 * @returns State and handlers; see {@link UseProcedureQuestionnaireReturn}.
 */
const useProcedureQuestionnaire = (): UseProcedureQuestionnaireReturn => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = getOrgIdForFetchExternalWorklist();
  const { id } = useParams();

  const {
    generalOrderDetails,
    generalOrderDetailsLoading,
    fetchAll,
    submissionData,
    submissionDataLoading,
  } = useDataAccumulatorForSingleOrder();

  const order = generalOrderDetails?.order ?? null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentAnswers, setCurrentAnswers] = useState<
    { id: string; answer: any }[]
  >([]);
  const faxPdfIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const {
    faxDataToSend,
    isFaxDataLoading,
    questionnaireStatus,
    applyQuestionnaireDocument,
  } = useFaxQuestionnaireLoader({
    orderId: id,
    orgId,
    submissionDataLoading,
    pgQuestionnaireDetails: submissionData?.submission?.questionnaire_details,
  });

  const { runUpdate: updateSubmissionQuestionnaire } =
    useUpdateOrderDetails<QuestionnaireSubmissionUpdate>({
      orderId: id ?? "",
      orgId: orgId ?? "",
      tableName: SingleOrderDetailsTableName.SUBMISSION,
    });

  const navigateBack = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    try {
      navigate(`/worklists/medical-pa-form/${id}`);
    } catch (error) {
      logError(error as Error);
    }
  };

  const startFaxPdfPolling = () => {
    console.log("startFaxPdfPolling");
    getFaxPdfLinkRecursively({
      orderId: id ?? "",
      orgId,
      navigate,
      intervalRef: faxPdfIntervalRef,
      setIsSubmitting,
    });
  };

  useEffect(() => {
    if (id) {
      fetchAll(id, orgId ?? "", { force: true });
      dispatch(getAllQuestionsForOrder(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  useEffect(() => {
    return () => {
      clearFaxPdfPoller(faxPdfIntervalRef);
    };
  }, []);

  const handleAnswersChange = (answers: { id: string; answer: any }[]) => {
    setCurrentAnswers(answers);
  };

  /**
   * Dual-writes merged questionnaire answers to Firestore + PG submission.
   *
   * @param updatedData - Existing questionnaire snapshot (includes `question_tree`)
   * @throws {Error} When org_id is missing, write fails, or no submission row updated
   */
  const updateFaxQuestionnaireDocument = useCallback(
    async (updatedData: Record<string, unknown>) => {
      try {
        const updateData = await persistFaxQuestionnaire({
          orderId: id ?? "",
          orgId,
          updatedData,
          currentAnswers,
          updatedBy: user?.email || "unknown",
          updateSubmissionQuestionnaire,
        });
        applyQuestionnaireDocument(updateData);
      } catch (error) {
        logError(error as Error, "Error updating fax questionnaire document");
        throw error;
      }
    },
    [
      applyQuestionnaireDocument,
      currentAnswers,
      id,
      orgId,
      updateSubmissionQuestionnaire,
      user?.email,
    ],
  );

  /**
   * Dual-writes answers (Firestore + PG), posts `/fax/submit`, then polls/navigates.
   *
   * @returns Resolves after submit attempt; errors are logged, not rethrown
   */
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (faxDataToSend) {
        await updateFaxQuestionnaireDocument(
          faxDataToSend as unknown as Record<string, unknown>,
        );
      }
      const formId = resolveFaxFormId(submissionData?.submission);
      if (formId) {
        await submitFaxFormQuestionaire({
          form_id: formId,
          order_id: order?.order_id ?? id ?? "",
        });
        startFaxPdfPolling();
      } else {
        navigate(`/worklists/final-document/${id}`);
      }
    } catch (error) {
      logError(error as Error, "Error saving fax form data and submitting");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    id,
    order,
    generalOrderDetailsLoading,
    faxDataToSend,
    isFaxDataLoading,
    questionnaireStatus,
    isSubmitting,
    handleAnswersChange,
    handleSubmit,
    navigateBack,
  };
};

export default useProcedureQuestionnaire;
